import {
  AppVersion,
  AppVersionCreatePayload,
  AppVersionPlatform,
  AppVersionUpdatePayload,
} from "@/domain/app-versions.interface";
import {
  AppVersionService,
  AppVersionsFindAllPayload,
} from "@/services/app-versions.service";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetCurrentVersion = (platform: string) => {
  const {
    data,
    isLoading: isLoadingAppVersion,
    isError: isErrorAppVersion,
    refetch: refetchAppVersion,
  } = useQuery<AppVersion, Error>({
    queryKey: ["app-version", platform],
    queryFn: () => AppVersionService.getCurrentVersion(platform),
  });

  return {
    appVersion: data,
    isLoadingAppVersion,
    isErrorAppVersion,
    refetchAppVersion,
  };
};

export const useAppVersions = (filters: AppVersionsFindAllPayload) => {
  const {
    data: appVersions,
    isLoading: isLoadingAppVersions,
    isFetching: isFetchingAppVersions,
    refetch: refetchAppVersions,
  } = useQuery({
    queryKey: ["parca-app-versions-list"],
    queryFn: () => AppVersionService.findAll(filters),
  });

  return {
    appVersions,
    isLoadingAppVersions,
    isFetchingAppVersions,
    refetchAppVersions,
  };
};

export const useCreateAppVersion = () => {
  return useMutation({
    mutationFn: (payload: AppVersionCreatePayload) =>
      AppVersionService.createOne(payload),
  });
};

export const useUpdateAppVersion = () => {
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: AppVersionUpdatePayload;
    }) => AppVersionService.updateOne({ id, payload }),
  });
};

export const useSetCurrentAppVersion = () => {
  return useMutation({
    mutationFn: ({
      id,
      platform,
    }: {
      id: string;
      platform: AppVersionPlatform;
    }) => AppVersionService.setCurrent({ id, platform }),
  });
};

export const useArchiveAppVersion = () => {
  return useMutation({
    mutationFn: (id: string) => AppVersionService.archiveOne(id),
  });
};
